/**
 * Modal for the two nesting interactions:
 *
 * - **pick-relation-type**: a child was just dropped inside a parent on
 *   the canvas, and there was no visible relation between the two
 *   concepts. Ask which relation (if any) to create.
 * - **pick-hidden-relation**: a child was just dragged out of its parent,
 *   and multiple relations between the two concepts are currently hidden
 *   on this diagram. Ask which one to make visible again.
 *
 * State lives in the UI store; the modal subscribes and renders when
 * `nestingPrompt` is non-null. Dismissing (Cancel/Escape/click-outside)
 * resolves the awaiting Promise with `null`.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getRegistry, resolveBilingual } from "../../registry";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { relationUsageElsewhere } from "./renestRelations";

export function NestingPromptModal() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const prompt = useUi((s) => s.nestingPrompt);
  const resolve = useUi((s) => s.resolveNestingPrompt);
  // Herschikken naar een andere container: de oude nesting-relatie(s) mee
  // verwijderen? Standaard aan; per nieuwe vraag opnieuw aan.
  const [removeOld, setRemoveOld] = useState(true);
  useEffect(() => {
    if (prompt) setRemoveOld(true);
  }, [prompt]);

  // Esc dismisses; click outside the dialog also dismisses.
  useEffect(() => {
    if (!prompt) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        resolve(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prompt, resolve]);

  if (!prompt) return null;

  const reg = getRegistry();
  const labelForRelationType = (id: string) => {
    const rt = reg.getRelationType(id);
    return rt ? resolveBilingual(rt.label, lang) : id;
  };
  const labelForRelationInstance = (id: string) => {
    const r = useWorkspaceData.getState().store.getRelation(id);
    if (!r) return id;
    const typeLabel = labelForRelationType(r.typeId);
    const instanceLabel = resolveBilingual(r.label, lang);
    return instanceLabel ? `${typeLabel} — "${instanceLabel}"` : typeLabel;
  };
  // Op welke diagram(men) komt een relatie voor? Voor de merge-prompt zodat de
  // modelleur ziet wat 'ie kiest. Geen enkele entry = "overal zichtbaar".
  const diagramsForRelation = (relId: string): string => {
    const names: string[] = [];
    for (const d of useWorkspaceData.getState().store.listDiagrams()) {
      const onIt = Object.entries(d.viewState).some(([k, ve]) => {
        if (ve.position !== undefined || ve.type === "object") return false;
        return (ve.ref ?? k) === relId;
      });
      if (onIt) names.push(resolveBilingual(d.name, lang) || t("canvas.nesting.unnamed"));
    }
    return names.length > 0
      ? t("canvas.nesting.onDiagrams", { names: names.join(", ") })
      : t("canvas.nesting.visibleEverywhere");
  };
  const labelForConceptInstance = (id: string): string => {
    const c = useWorkspaceData.getState().store.getConcept(id);
    if (!c) return id;
    return resolveBilingual(c.name, lang) || id;
  };
  // Op welke diagram(men) staat een concept? Concept-entries dragen een
  // `position` of `type === "object"`; relatie-entries niet.
  const diagramsForConcept = (cid: string): string => {
    const names: string[] = [];
    for (const d of useWorkspaceData.getState().store.listDiagrams()) {
      const onIt = Object.entries(d.viewState).some(([k, ve]) => {
        const isObject = ve.position !== undefined || ve.type === "object";
        if (!isObject) return false;
        return (ve.ref ?? k) === cid;
      });
      if (onIt) names.push(resolveBilingual(d.name, lang) || t("canvas.nesting.unnamed"));
    }
    return names.length > 0
      ? t("canvas.nesting.onDiagrams", { names: names.join(", ") })
      : t("canvas.nesting.notPlaced");
  };

  const title =
    prompt.kind === "pick-relation-type"
      ? t("canvas.nesting.title.pickRelationType")
      : prompt.kind === "pick-hidden-relation"
        ? t("canvas.nesting.title.pickHiddenRelation")
        : prompt.kind === "merge-relation"
          ? t("canvas.nesting.title.mergeRelation")
          : prompt.kind === "merge-concept"
            ? t("canvas.nesting.title.mergeConcept")
            : t("canvas.nesting.title.pickExistingOrNew");
  const description =
    prompt.kind === "pick-relation-type"
      ? t("canvas.nesting.description.pickRelationType")
      : prompt.kind === "pick-hidden-relation"
        ? t("canvas.nesting.description.pickHiddenRelation")
        : prompt.kind === "merge-relation"
          ? t("canvas.nesting.description.mergeRelation")
          : prompt.kind === "merge-concept"
            ? t("canvas.nesting.description.mergeConcept")
            : t("canvas.nesting.description.pickExistingOrNew");

  const items =
    prompt.kind === "pick-relation-type"
      ? prompt.relationTypeIds.map((id) => ({ id, label: labelForRelationType(id) }))
      : prompt.kind === "pick-hidden-relation"
        ? prompt.relationIds.map((id) => ({ id, label: labelForRelationInstance(id) }))
        : prompt.kind === "merge-relation"
          ? prompt.candidateRelationIds.map((id) => ({
              id,
              label: `${labelForRelationInstance(id)} · ${diagramsForRelation(id)}`,
            }))
          : prompt.kind === "merge-concept"
            ? prompt.candidateConceptIds.map((id) => ({
                id,
                label: `${labelForConceptInstance(id)} · ${diagramsForConcept(id)}`,
              }))
            : prompt.existingRelationIds.map((id) => ({ id, label: labelForRelationInstance(id) }));

  return (
    <div
      className="ledger fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40"
      onClick={() => resolve(null)}
      role="dialog"
      aria-modal="true"
    >
      <div
        // Bij veel kandidaten — denk aan dertig samenvoegpunten met dezelfde
        // naam — groeide deze kaart buiten het scherm en vielen de knoppen
        // eronder weg: je kon niet meer kiezen én niet meer annuleren.
        // Vandaar een maximumhoogte met de lijst als het scrollende deel.
        className="flex max-h-[85vh] w-full max-w-md flex-col rounded-lg border border-(--ld-control) bg-(--ld-surface) p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h2 className="font-semibold text-(--ld-text) text-base">{title}</h2>
        <p className="mt-1 text-(--ld-muted) text-sm">{description}</p>
        {prompt.kind === "pick-relation-type" && prompt.oldNesting && (
          <OldNestingChoice
            oldNesting={prompt.oldNesting}
            checked={removeOld}
            onChange={setRemoveOld}
            labelForConcept={labelForConceptInstance}
            labelForRelation={labelForRelationInstance}
          />
        )}
        <ul className="mt-4 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
          {items.length === 0 ? (
            <li className="rounded bg-(--ld-raised) px-3 py-2 text-(--ld-muted) text-sm">
              {t("canvas.nesting.noOptions")}
            </li>
          ) : (
            items.map((it) => (
              <li key={it.id}>
                <button
                  type="button"
                  className="w-full rounded border border-(--ld-border) bg-(--ld-surface) px-3 py-2 text-left text-sm hover:border-(--ld-accent) hover:bg-(--ld-active)"
                  onClick={() =>
                    resolve(it.id, prompt.kind === "pick-relation-type" ? removeOld : undefined)
                  }
                >
                  {it.label}
                </button>
              </li>
            ))
          )}
        </ul>
        {prompt.kind === "pick-existing-or-new" && (
          <button
            type="button"
            className="mt-2 w-full shrink-0 rounded border border-transparent bg-(--ld-positive-bg) px-3 py-2 text-left font-medium text-(--ld-positive-ink) text-sm hover:bg-(--ld-positive-bg)"
            onClick={() => resolve("new")}
          >
            {t("canvas.nesting.newRelation")}
          </button>
        )}
        {(prompt.kind === "merge-relation" || prompt.kind === "merge-concept") && (
          <button
            type="button"
            className="mt-2 w-full shrink-0 rounded border border-transparent bg-(--ld-notice-bg) px-3 py-2 text-left font-medium text-(--ld-notice-ink) text-sm hover:bg-(--ld-notice-bg)"
            onClick={() => resolve("keep")}
          >
            {t("canvas.nesting.keepBoth")}
          </button>
        )}
        <div className="mt-4 flex shrink-0 justify-end gap-2">
          <button
            type="button"
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1 text-(--ld-text) text-sm hover:bg-(--ld-raised)"
            onClick={() =>
              resolve(null, prompt.kind === "pick-relation-type" ? removeOld : undefined)
            }
          >
            {prompt.kind === "pick-relation-type"
              ? t("canvas.nesting.noRelation")
              : t("settings.common.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Het vinkje bij herschikken: de relatie(s) naar de oude container uit het
 * model verwijderen. Toont waar ze nog meer te zien zijn, want verwijderen
 * haalt ze ook daar weg.
 */
function OldNestingChoice(props: {
  oldNesting: { parentConceptId: string; relationIds: string[] };
  checked: boolean;
  onChange: (v: boolean) => void;
  labelForConcept: (id: string) => string;
  labelForRelation: (id: string) => string;
}) {
  const { t, i18n } = useTranslation();
  const store = useWorkspaceData.getState().store;
  const activeDiagramId = useWorkspaceData.getState().activeDiagramId ?? "";
  const diagrams = store.listDiagrams();
  const elsewhere = new Set<string>();
  let everywhere = false;
  for (const relId of props.oldNesting.relationIds) {
    const usage = relationUsageElsewhere(relId, diagrams, activeDiagramId);
    if (usage.everywhere) everywhere = true;
    else
      for (const d of usage.diagrams)
        elsewhere.add(resolveBilingual(d.name, i18n.language) || d.id);
  }
  return (
    <label className="mt-3 flex items-start gap-2 rounded border border-transparent bg-(--ld-notice-bg) px-3 py-2 text-(--ld-notice-ink) text-sm">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={props.checked}
        onChange={(e) => props.onChange(e.target.checked)}
      />
      <span>
        {t("canvas.nesting.removeOld", {
          count: props.oldNesting.relationIds.length,
          parent: props.labelForConcept(props.oldNesting.parentConceptId),
          relations: props.oldNesting.relationIds.map(props.labelForRelation).join(", "),
        })}
        <span className="mt-0.5 block text-(--ld-notice-ink) text-xs">
          {everywhere
            ? t("canvas.nesting.removeOldEverywhere")
            : elsewhere.size > 0
              ? t("canvas.nesting.removeOldElsewhere", { names: [...elsewhere].join(", ") })
              : t("canvas.nesting.removeOldOnlyHere")}
        </span>
      </span>
    </label>
  );
}
